import { Component, Input, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { FundId } from './../../../types/fund-identifier.type';
import { SalesChargeBreakPoint } from '@models';
import { NAV } from '@products/models/nav';
import { TranslateService } from '@shared/translate/translate.service';
import { InfoItem, ResponsiveService } from '@frk/eds-components';
import { PricingConfig } from '@types';
import { EMDASH } from '@products/utils/constants/product.constants';
import { SiteConfigService } from '@services/site-config.service';
import { PricingService } from '@products/pricing-distribution/services/pricing.service';

@Component({
  selector: 'ft-pricing-breakpoints',
  templateUrl: './pricing-breakpoints.component.html',
  styleUrls: ['./pricing-breakpoints.component.scss'],
})
export class PricingBreakpointsComponent implements OnInit {
  @Input() navData: NAV;
  @Input() salesChargeBreakPointsData: SalesChargeBreakPoint[];
  @Input() fundId: FundId;
  @Input() config: PricingConfig;
  weeksData: InfoItem[];
  highNavData: InfoItem[];
  lowNavData: InfoItem[];
  isHandheld$: Observable<boolean>;
  titleLabel: string;
  premiumDiscountData: InfoItem[];
  showWeekRange: boolean;
  showPremiumDiscountData: boolean;
  showSalesChargeBreakPointsData: boolean;

  constructor(
    private translateService: TranslateService,
    protected responsiveService: ResponsiveService,
    // private siteConfigService: SiteConfigService,
    private pricingService: PricingService
  ) {}

  ngOnInit(): void {
    this.weeksData = this.getWeeksDataInfoItems();
    this.premiumDiscountData = this.getPremiumDiscountDataInfoItems();
    this.showPremiumDiscountData = this.pricingService.showPremiumDiscountData(
      this.premiumDiscountData
    );
    this.titleLabel =
      this.salesChargeBreakPointsData &&
      this.salesChargeBreakPointsData.length > 0
        ? 'products.daily-fund-prices-and-breakpoints'
        : 'products.daily-fund-prices';

    this.showSalesChargeBreakPointsData = this.pricingService.showSalesChargeBreakPointsData(
      this.salesChargeBreakPointsData
    );
    this.showWeekRange = this.pricingService.showWeekRange(this.config);
  }

  /**
   * 52 week nav data
   */
  getWeeksDataInfoItems(): InfoItem[] {
    const itemsSrc: InfoItem[] = [
      {
        label: this.translateService.instant('pds.HIGH_NAV_VALUE_YTD'),
        val: this.navData.highestNavValueIn52Weeks,
        asOfLabel: this.translateService.instant('products.as-on'),
        asOfDate: this.navData.highestNavValueDateIn52Weeks,
        caveatPlacement: 'Price:HighestNavPrice',
      },
    ];
    if (this.config.showMarketPriceData) {
      itemsSrc.push({
        label: this.translateService.instant('products.highest-market-price'),
        val: this.navData.highestMarketPriceValueIn52Weeks,
        asOfLabel: this.translateService.instant('products.as-on'),
        asOfDate: this.navData.highestMarketPriceValueDateIn52Weeks,
        caveatPlacement: 'Price:HighestMarketPrice',
      });
    }
    itemsSrc.push({
      label: this.translateService.instant('pds.LOW_NAV_VALUE_YTD'),
      val: this.navData.lowestNavValueIn52Weeks,
      asOfLabel: this.translateService.instant('products.as-on'),
      asOfDate: this.navData.lowestNavValueDateIn52Weeks,
      caveatPlacement: 'Price:LowestNavPrice',
    });
    if (this.config.showMarketPriceData) {
      itemsSrc.push({
        label: this.translateService.instant('products.lowest-market-price'),
        val: this.navData?.lowestMarketPriceValueIn52Weeks,
        asOfLabel: this.translateService.instant('products.as-on'),
        asOfDate: this.navData?.lowestMarketPriceValueDateIn52Weeks,
        caveatPlacement: 'Price:LowestMarketPrice',
      });
    }
    return itemsSrc;
  }

  getPremiumDiscountDataInfoItems(): InfoItem[] {
    const itemsSrc: InfoItem[] = [];
    if (this.config.showPremiumDiscountData) {
      if (
        this.navData.medianBidAskSpread &&
        this.navData.medianBidAskSpread !== EMDASH
      ) {
        itemsSrc.push({
          label: this.translateService.instant(
            'products.median-bid-ask-spread',
            this.fundId
          ),
          val: this.navData.medianBidAskSpread,
          caveatPlacement: 'Price:MedianBidAskSpreadPrice',
          tooltip: this.translateService.instant(
            'products.median-bid-ask-spread-tooltip'
          ),
        });
      }
      if (this.navData.bidAskSpread && this.navData.bidAskSpread !== EMDASH) {
        itemsSrc.push({
          label: this.translateService.instant(
            'products.bid-ask-spread',
            this.fundId
          ),
          val: this.navData.bidAskSpread,
          caveatPlacement: 'Price:BidAskSpreadPrice',
        });
      }
      if (
        this.navData.premiumDiscountAvg &&
        this.navData.premiumDiscountAvg !== EMDASH
      ) {
        itemsSrc.push({
          label: this.translateService.instant(
            'products.premium-discount-average',
            this.fundId
          ),
          val: this.navData.premiumDiscountAvg,
          caveatPlacement: 'Price:PremiumDiscountAveragePrice',
        });
      }

      if (this.navData.premiumDiscountPct) {
        itemsSrc.push({
          label: this.translateService.instant(
            'products.premium-discount-percent',
            this.fundId
          ),
          val: this.navData.premiumDiscountPct,
          caveatPlacement: 'Price:PremiumDiscountPercentPrice',
        });
      }
    }
    return itemsSrc;
  }
}
